// scheduling/hooks/useWindowCapacity.js
// Capacity inputs (getDayCap / dayHoursByStaff / getContract) for an arbitrary date
// window, for surfaces that don't already hold them.
//
// The Dashboard loads the firm-wide schedule with no date bound and passes its own
// helpers in as `provided`, so this hook does nothing there. The Reporting section's
// Unscheduled Hours report opens the same ScheduleChooserModal without any of that data,
// so here the hook fetches exactly the window being planned — one bootstrap request,
// re-run (debounced) when the window moves.

import { useEffect, useState } from 'react';
import { fetchBootstrap } from '../services/schedData';
import {
  makeGetDayCap, makeGetContract, buildPtoByStaffDay, buildHolidayByDate, buildDayHoursByStaff,
} from '../utils/capacity';

const EMPTY = [];

/**
 * @param {string} startISO  first day of the window (YYYY-MM-DD)
 * @param {string} endISO    last day of the window
 * @param {object|null} provided  { getDayCap, dayHoursByStaff, getContract } when the caller has them
 * @returns {{ getDayCap, dayHoursByStaff, getContract } | null}  null while loading
 */
export function useWindowCapacity({ startISO, endISO, provided }) {
  const [fetched, setFetched] = useState(null);

  useEffect(() => {
    if (provided || !startISO || !endISO) return undefined;
    let cancelled = false;
    const range = { startDate: startISO, endDate: endISO };
    const t = setTimeout(async () => {
      try {
        const boot = await fetchBootstrap([
          { type: 'staff', args: {} },
          { type: 'schedule', args: range },
          { type: 'calendars', args: {} },
          { type: 'pto', args: range },
          { type: 'holidays', args: {} },
          { type: 'global_assignments', args: {} },
        ]);
        if (cancelled) return;
        const d = boot?.data || {};
        const staff = d.staff || EMPTY;
        const calendars = d.calendars || EMPTY;
        const years = new Set();
        for (let y = Number(startISO.slice(0, 4)); y <= Number(endISO.slice(0, 4)); y++) years.add(y);
        setFetched({
          key: `${startISO}|${endISO}`,
          getDayCap: makeGetDayCap({
            staff, globalCalendars: calendars,
            ptoByStaffDay: buildPtoByStaffDay(d.pto || EMPTY),
            holidayByDate: buildHolidayByDate(d.holidays || EMPTY, years),
          }),
          dayHoursByStaff: buildDayHoursByStaff(d.schedule || EMPTY, d.global_assignments || EMPTY, staff),
          getContract: makeGetContract({ staff, globalCalendars: calendars }),
        });
      } catch {
        // Capacity cues are advisory — without them the chooser still schedules, it just
        // can't say who has room, so a failed fetch leaves the cues hidden.
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [provided, startISO, endISO]);

  // Data fetched for a previous window would read every day outside it as empty, i.e.
  // overstate free hours — report "not loaded" instead until this window arrives.
  if (provided) return provided;
  return fetched?.key === `${startISO}|${endISO}` ? fetched : null;
}
